import * as React from "react";
import { useRecordContext, useGetOne, Loading, Error, useNotify, useRefresh } from "react-admin";
import ApiClient from "../../ApiClient";
import API from "../../Routes";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Chip,
  Select,
  MenuItem,
  Button,
  FormControl,
} from "@mui/material";

const EXTRA_TIME_OPTIONS = [0, 25, 50, 75, 100, 125, 150, 175, 200];

/** Use the value from the API as-is so the dropdown shows the current extra time (e.g. 25%) when entering the tab. */
const getExtensionTimePercentage = (val) => {
  if (val == null || typeof val !== "number") return 0;
  return Math.floor(val);
};

const buildAssessments = (items, idPrefix) =>
  (items || []).map((a, idx) => ({
    id: `${idPrefix}-${idx}`,
    assessmentId: a.assessmentId ?? a.id,
    name: a.name,
    type: a.type,
    status: a.status,
    extensionTimePercentage: getExtensionTimePercentage(a.extensionTimePercentage),
  }));

const CandidateAssessmentsPanel = () => {
  const record = useRecordContext();
  const candidateId = record?.id;
  const [extraTimeValues, setExtraTimeValues] = React.useState({});
  const notify = useNotify();
  const refresh = useRefresh();

  const { data: scoresData, isLoading: scoresLoading, error: scoresError } =
    useGetOne("scores", { id: candidateId }, { enabled: !!candidateId, retry: false });

  const { data: campaignData } = useGetOne(
    "campaigns",
    { id: record?.campaignId },
    { enabled: !!record?.campaignId }
  );

  if (!candidateId) return null;

  if (scoresLoading) return <Loading />;
  if (scoresError) return <Error error={scoresError} />;

  const isThriveCampaign = campaignData?.assessmentsSource === "THRIVE";

  const assessments = scoresData
    ? [
        ...buildAssessments(scoresData.assessmentResults, "assessment"),
        ...buildAssessments(scoresData.skillAssessmentResults, "skill"),
      ]
    : [];

  if (!assessments.length) {
    return (
      <Typography variant="body2">No assessments yet.</Typography>
    );
  }

  const handleExtraTimeChange = (assessmentRowId, value) => {
    setExtraTimeValues((prev) => ({ ...prev, [assessmentRowId]: value }));
  };

  const handleAddExtraTime = async (assessment) => {
    if (!assessment.assessmentId) {
      notify("Assessment ID is missing", { type: "error" });
      return;
    }
    const timeExtensionPercentage =
      extraTimeValues[assessment.id] ?? assessment.extensionTimePercentage;
    try {
      await ApiClient.fetch(API.CANDIDATES.EXTEND_TIME(candidateId), {
        method: "POST",
        body: {
          extendAssessmentTimeRequests: [
            {
              assessmentId: assessment.assessmentId,
              timeExtensionPercentage,
            },
          ],
        },
      });
      notify("Extra time added successfully");
      refresh();
    } catch (error) {
      notify(
        `Failed to add extra time: ${error?.message || error}`,
        { type: "error" }
      );
    }
  };

  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>
            <strong>Name</strong>
          </TableCell>
          <TableCell>
            <strong>Type</strong>
          </TableCell>
          <TableCell>
            <strong>Status</strong>
          </TableCell>
          <TableCell>
            <strong>Current extra time</strong>
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {assessments.map((assessment) => {
          const isCompleted = assessment.status === "COMPLETED";
          const isDropdownEnabled =
            assessment.type !== "PERSONALITY" &&
            !isCompleted &&
            isThriveCampaign;
          const currentExtraTime =
            extraTimeValues[assessment.id] ?? assessment.extensionTimePercentage;
          const hasExtraTimeChanged =
            Number(currentExtraTime) !== Number(assessment.extensionTimePercentage);
          const isButtonEnabled = isDropdownEnabled && hasExtraTimeChanged;
          // Include the assessment's current value in options so it appears as the selected option when entering the tab (e.g. 25%)
          const dropdownOptions = [
            ...new Set([...EXTRA_TIME_OPTIONS, currentExtraTime]),
          ].sort((a, b) => a - b);

          return (
            <TableRow key={assessment.id}>
              <TableCell>{assessment.name}</TableCell>
              <TableCell>{assessment.type}</TableCell>
              <TableCell>
                <Chip
                  label={assessment.status}
                  size="small"
                  color={isCompleted ? "success" : "default"}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={currentExtraTime ?? 0}
                    onChange={(e) =>
                      handleExtraTimeChange(assessment.id, Number(e.target.value))
                    }
                    disabled={!isDropdownEnabled}
                    renderValue={(v) => `${v}%`}
                  >
                    {dropdownOptions.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}%
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!isButtonEnabled}
                  onClick={() => handleAddExtraTime(assessment)}
                >
                  Add extra time
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default CandidateAssessmentsPanel;
